import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import type { IssueAttachmentPlacement } from "@/domains/pm/utils/protocolAttachments";
import { fill } from "@/lib/i18n/fill";
import type { Translations } from "@/lib/i18n/types";
import type { ProtocolIssueAttachmentsStatus } from "./useProtocolAttachments";

type ProtocolCopy = Translations["pm"]["issues"]["protocol"];

export interface IssueAttachmentsSetting extends IssueAttachmentPlacement {
  enabled: boolean;
}

/**
 * The issue table's "Attachments" sub-option: whether the issues' own files
 * print, and where — as thumbnails in a last table column, as full pages in
 * the appendix, or both. One placement always stays ticked: with neither, the
 * option would be on and print nothing.
 */
export function ProtocolIssueAttachmentsOption({
  value,
  onChange,
  status,
  fileCount,
  onRetry,
  copy,
}: {
  value: IssueAttachmentsSetting;
  onChange: (value: IssueAttachmentsSetting) => void;
  status: ProtocolIssueAttachmentsStatus;
  /** Files on the ticked issues, once read. */
  fileCount: number;
  onRetry: () => void;
  copy: ProtocolCopy;
}) {
  const labels = copy.attachments;
  const placements: Array<{ key: "inTable" | "inAppendix"; label: string }> = [
    { key: "inTable", label: labels.inTable },
    { key: "inAppendix", label: labels.inAppendix },
  ];
  const onlyOne = Number(value.inTable) + Number(value.inAppendix) === 1;

  return (
    <div className="space-y-2 pt-1">
      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <Checkbox
          checked={value.enabled}
          onCheckedChange={(checked) => onChange({ ...value, enabled: checked === true })}
        />
        {labels.issueOption}
        <span className="text-xs text-muted-foreground">— {labels.issueOptionHelp}</span>
      </label>
      {value.enabled && (
        <div className="ml-6 space-y-2">
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {placements.map(({ key, label }) => (
              <label key={key} className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={value[key]}
                  disabled={value[key] && onlyOne}
                  onCheckedChange={(checked) => onChange({ ...value, [key]: checked === true })}
                />
                {label}
              </label>
            ))}
          </div>
          {status === "loading" && (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {labels.issuesLoading}
            </p>
          )}
          {status === "error" && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-destructive">
              {labels.issuesError}
              <Button variant="outline" size="sm" onClick={onRetry}>
                {copy.retry}
              </Button>
            </div>
          )}
          {status === "ready" && (
            <p className="text-xs text-muted-foreground">
              {fileCount === 0
                ? labels.issuesNone
                : fill(labels.issuesFound, { count: String(fileCount) })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
